import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { differenceInCalendarDays, isValid, parseISO, startOfDay } from "date-fns";
import useDataBean from "../../../../core/services/dataBean/useDataBean";
import { _Project } from "../../../../core/services/dataBean/EntityCatalogVdos";

export type Project = {
    AdminFee: number;
    BpinCode: number;
    IDAccount: number;
    IDProject: number;
    ManagementType: number;
    IDPaymentMethod: number;
    State: number;

    TotalValue: number;
    PFValue: number;
    TotalPF: number;
    MaxAmmount: number;
    Disponible: number | null;
    LeftValue: number;
    Nit: number;
    TotalPAA: number;

    Since: string;
    UptoDate: string; //fecha final
    UptoDateAgreementBalance: string; //fecha final de modificaciones
    SettlementDate: string; //fecha de liquidacion
    InitDate: string;

    IsSettlement: boolean;

    Description: string;
    ObjectText: string;
    EntityName: string;
    ExternalRPDate: string;
    IDBusinessTypeName: string;
    Name: string;
    IDPaymentMethodName: string;
    ExternalRP: string;
    Code: string;
    IDAgreementClassificationName: string;
    IDLnProjectIDName: string | null;

    [key: string]: unknown;
};

export enum ProjectValidationType {
    ADQUISITION_ITEM = "ADQUISITION_ITEM",
    ADQUISITION_CONTRACT = "ADQUISITION_CONTRACT",
}

export type AdquisitionItemProjectValidationInput = {
    type: ProjectValidationType.ADQUISITION_ITEM;
    totalValue: number;
};

export type AdquisitionContractProjectValidationInput = {
    type: ProjectValidationType.ADQUISITION_CONTRACT;
    totalValue: number;
};

export type ProjectValidationInput = AdquisitionItemProjectValidationInput | AdquisitionContractProjectValidationInput;

export type ProjectValidationAlert = {
    severity: "error" | "warning" | "info";
    message: string;
};
export type ProjectNotesAlert = {
    severity: "error" | "warning" | "info";
    message: string;
};

const buildProjectDateNote = (
    rawDate: string,
    dateLabel: string,
    messagePrefix = ""
): ProjectNotesAlert => {
    const displayDate = rawDate.slice(0, 10);
    const parsedDate = parseISO(displayDate);

    if (!isValid(parsedDate)) {
        return {
            severity: "error",
            message: `${messagePrefix}La fecha de ${dateLabel} del proyecto no es válida`,
        };
    }

    const remainingDays = differenceInCalendarDays(parsedDate, startOfDay(new Date()));
    const absoluteDays = Math.abs(remainingDays);
    const dayLabel = absoluteDays === 1 ? "día" : "días";

    if (remainingDays < 0) {
        return {
            severity: "error",
            message: `${messagePrefix}La fecha de ${dateLabel} del proyecto venció hace ${absoluteDays} ${dayLabel} (${displayDate})`,
        };
    }

    if (remainingDays === 0) {
        return {
            severity: "warning",
            message: `${messagePrefix}La fecha de ${dateLabel} del proyecto es hoy (${displayDate})`,
        };
    }

    return {
        severity: remainingDays <= 30 ? "warning" : "info",
        message: `${messagePrefix}Al proyecto le quedan aproximadamente ${remainingDays} ${dayLabel} para su ${dateLabel} (${displayDate})`,
    };
};

export type ProjectValidationResult = {
    canModify: boolean;
    alerts: ProjectValidationAlert[];
};

function useProjectValidations(IDProject?: number | null) {
    const { instance } = useDataBean(_Project);
    const [project, setProject] = useState<Partial<Project> | null>(null);
    const [canModify, setCanModify] = useState(false);
    const [alerts, setAlerts] = useState<ProjectValidationAlert[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadedProjectID, setLoadedProjectID] = useState<number | null>(null);

    useEffect(() => {

        let isCurrentRequest = true;

        setProject(null);
        setLoadedProjectID(null);
        setCanModify(false);
        setAlerts([]);
        if (!IDProject) {
            setIsLoading(false);
            return () => {
                isCurrentRequest = false;
            };
        }

        setIsLoading(true);

        instance.getProjectRender(1, 10, { IDProject, State: 1 })
            .then((response: any) => {
                if (!isCurrentRequest) return;

                const projectResponse = response?.List?.[0];
                setProject(projectResponse ? projectResponse.DataBeanProperties : null);
                setLoadedProjectID(IDProject);
            })
            .catch(() => {
                if (!isCurrentRequest) return;

                setProject(null);
                toast.error("No se pudo cargar la información del proyecto");
            })
            .finally(() => {
                if (isCurrentRequest) setIsLoading(false);
            });

        return () => {
            isCurrentRequest = false;
        };
    }, [IDProject]);

    const notes = useMemo<ProjectNotesAlert[]>(() => {
        if (!project || !IDProject || loadedProjectID !== IDProject) return [];

        const settlementDate = project.SettlementDate?.trim();
        const validityDate =
            project.UptoDateAgreementBalance?.trim() ||
            project.UptoDate?.trim();

        if (project.IsSettlement) {
            if (!settlementDate) {
                return [{
                    severity: "error",
                    message: "El proyecto no tiene fecha de liquidación",
                }];
            }

            return [{
                ...buildProjectDateNote(
                    settlementDate,
                    "liquidación",
                    "El proyecto se encuentra en liquidación. "
                ),
            }];
        }

        if (!validityDate) {
            return [{
                severity: "error",
                message: "El proyecto no tiene fecha de vigencia",
            }];
        }

        return [buildProjectDateNote(validityDate, "finalización de vigencia")];
    }, [IDProject, loadedProjectID, project]);

    const validate = useCallback((input: ProjectValidationInput): ProjectValidationResult => {
        const validationAlerts: ProjectValidationAlert[] = [];

        if (!IDProject) {
            validationAlerts.push({
                severity: "error",
                message: "Debe seleccionar un proyecto",
            });
        } else if (isLoading || loadedProjectID !== IDProject) {
            validationAlerts.push({
                severity: "warning",
                message: "La información del proyecto aún se está cargando",
            });
        } else if (!project) {
            validationAlerts.push({
                severity: "error",
                message: "No se encontró información del proyecto seleccionado",
            });
        } else {
            switch (input.type) {
                case ProjectValidationType.ADQUISITION_ITEM: {
                    const availableValue = project.Disponible;

                    if (availableValue == null) {
                        validationAlerts.push({
                            severity: "error",
                            message: "El proyecto no tiene valor disponible",
                        });
                    } else if (input.totalValue > Number(availableValue)) {
                        validationAlerts.push({
                            severity: "error",
                            message: "El valor estimado no puede superar el disponible del proyecto",
                        });
                    }
                    break;
                }
            }
        }

        const result = {
            canModify: validationAlerts.length === 0,
            alerts: validationAlerts,
        };

        setCanModify(result.canModify);
        setAlerts(result.alerts);

        return result;
    }, [IDProject, isLoading, loadedProjectID, project]);

    const showAlerts = useCallback((validationAlerts: ProjectValidationAlert[] = alerts) => {
        validationAlerts.forEach(({ severity, message }) => {
            if (severity === "warning") {
                toast.warn(message, { autoClose: false });
            } else if (severity === "info") {
                toast.info(message, { autoClose: false });
            } else {
                toast.error(message, { autoClose: false });
            }
        });
    }, [alerts]);

    return {
        project,
        isLoading,
        canModify,
        alerts,
        validate,
        showAlerts,
        notes
    };
}

export default useProjectValidations;
